import { TOAST_CONFIGS } from "@/config/toasts";
import { http } from "@/http";
import jwtDecode from "jwt-decode";
import { useToast } from "vue-toastification";
import i18n from "@/i18n";

// mutation types
const SET_PERMISSIONS = "SET_PERMISSIONS";
const SET_PERMISSION_TOKEN = "SET_PERMISSION_TOKEN";

// links expire at the "exp" claim, warn during the last 3 days (NM-23504)
const TOKEN_MAX_AGE_MILLIS = 14 * 24 * 60 * 60 * 1000;
const TOKEN_EXPIRY_WARNING_MILLIS = 3 * 24 * 60 * 60 * 1000;
// legacy links without iat/exp stay usable through 2026-08-31 (NM-23504)
const LEGACY_TOKEN_EXPIRES_AT = new Date(2026, 7, 31, 23, 59, 59);

// initial state
const state = () => ({
  permissionToken: null,
  permissionTokenPayload: null,
  permissions: [],
});

// getters
const getters = {
  getAll(state) {
    return state.permissions;
  },
  getPermissionToken(state) {
    return state.permissionToken;
  },
  getPermissionTokenPayload(state) {
    return state.permissionTokenPayload;
  },
  isLegacyToken(state) {
    const payload = state.permissionTokenPayload;
    return payload != null && !payload.exp && !payload.iat;
  },
  tokenExpiresAt(state) {
    const payload = state.permissionTokenPayload;
    if (!payload) {
      return null;
    }
    if (payload.exp) {
      return new Date(payload.exp * 1000);
    }
    if (payload.iat) {
      return new Date(payload.iat * 1000 + TOKEN_MAX_AGE_MILLIS);
    }
    // legacy token without an issue date -> grace period
    return LEGACY_TOKEN_EXPIRES_AT;
  },
  isTokenExpired(state, getters) {
    const expiresAt = getters.tokenExpiresAt;
    return expiresAt != null && expiresAt.getTime() <= Date.now();
  },
  isTokenExpiringSoon(state, getters) {
    const expiresAt = getters.tokenExpiresAt;
    if (expiresAt == null || getters.isTokenExpired) {
      return false;
    }
    // legacy links always show the expiry notice until the grace period ends
    if (getters.isLegacyToken) {
      return true;
    }
    return expiresAt.getTime() - Date.now() <= TOKEN_EXPIRY_WARNING_MILLIS;
  },
};

// actions
const actions = {
  clearData({ commit }) {
    commit(SET_PERMISSIONS, []);
  },
  async setPermissions({ commit, getters }, token = null) {
    if (token == null) {
      token = localStorage.getItem(`permission_token`);
    } else {
      localStorage.setItem(`permission_token`, token);
    }
    commit(SET_PERMISSION_TOKEN, token);

    if (getters.isTokenExpired) {
      commit(SET_PERMISSIONS, []);
      return;
    }

    const response = await http
      .post(`/api/v1/permissions`, {
        permissionToken: token,
      })
      .catch((err) => {
        console.error(err);
        useToast().error(
          i18n.global.t("error.loadingPermissions"),
          TOAST_CONFIGS.FATAL_ERROR
        );
      });
    const permissions = response?.data?.data ?? [];
    commit(SET_PERMISSIONS, permissions);
  },
  setPermissionToken({ commit, dispatch }, token) {
    commit(SET_PERMISSION_TOKEN, token);
    return dispatch("permissions/setPermissions");
  },
};

// mutations
const mutations = {
  [SET_PERMISSIONS](state, permissions) {
    state.permissions = permissions;
  },
  [SET_PERMISSION_TOKEN](state, token) {
    state.permissionToken = token;
    try {
      state.permissionTokenPayload = jwtDecode(token);
    } catch (err) {
      console.error("invalid token provided");
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
